import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Button, Card, Col, DatePicker, Form, Grid, Input, Modal, Row, Select, Space,
    Statistic, Switch, Table, Tabs, Tag, Typography, Upload, message,
} from 'antd';
import { DownloadOutlined, FileExcelOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import client from '../api/client';
import RiceAllowanceUploadReview from './RiceAllowanceUploadReview';

const { Text } = Typography;

const BASE = '/transactions/rice-allowances/monthly-upload';
const MAX_ROWS = 1000;
const COLUMN_LABELS = { batch: 'Batch', employee_name: 'Employee Name', kg: 'Batch KG', rice_item: 'Rice Item', price: 'Price' };
const REQUIRED_COLUMNS = ['employee_name', 'kg', 'rice_item', 'price'];
const COLUMN_ALIASES = {
    batch: ['batch', 'batchlabel', 'batchname', 'group'],
    employee_name: ['employeename', 'employee', 'name', 'fullname'],
    kg: ['batchkg', 'kg', 'allowancekg', 'quantitykg', 'qtykg'],
    rice_item: ['riceitem', 'item', 'rice', 'product', 'itemname'],
    price: ['price', 'unitprice', 'priceperkg'],
};
const statusColor = { VALID: 'success', WARNING: 'warning', INVALID: 'error' };
const draftStatusColor = { DRAFT: 'default', APPROVED: 'processing', RELEASED: 'success', CANCELLED: 'error' };

const normalizeHeader = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const money = (value) => `₱${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const kgText = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const apiError = (error, fallback) => {
    const data = error.response?.data;
    const first = data?.errors ? Object.values(data.errors).flat()[0] : null;
    return first || data?.message || fallback;
};

/**
 * Reads the first sheet. Cell values are passed on exactly as the workbook holds them
 * (numbers stay numbers, text stays text); the server decides what each value means.
 */
const parseWorkbook = async (file) => {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!sheet) throw new Error('The workbook has no sheets.');

    const records = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });
    if (!records.length) throw new Error('The spreadsheet has no data rows.');

    const columnFor = {};
    Object.keys(records[0]).forEach((header) => {
        const key = normalizeHeader(header);
        const field = Object.keys(COLUMN_ALIASES).find((name) => COLUMN_ALIASES[name].includes(key));
        if (field && !columnFor[field]) columnFor[field] = header;
    });

    const missing = REQUIRED_COLUMNS.filter((field) => !columnFor[field]);
    if (missing.length) {
        throw new Error(`Missing column(s): ${missing.map((field) => COLUMN_LABELS[field]).join(', ')}.`);
    }

    const rows = records
        .map((record, index) => ({
            row: index + 2,
            batch: columnFor.batch ? record[columnFor.batch] : '',
            employee_name: record[columnFor.employee_name],
            kg: record[columnFor.kg],
            rice_item: record[columnFor.rice_item],
            price: record[columnFor.price],
        }))
        .filter((row) => ['batch', 'employee_name', 'kg', 'rice_item', 'price'].some((field) => String(row[field] ?? '').trim() !== ''));

    if (!rows.length) throw new Error('The spreadsheet has no data rows.');
    if (rows.length > MAX_ROWS) throw new Error(`The spreadsheet has ${rows.length} rows; the limit is ${MAX_ROWS}.`);

    return rows;
};

const downloadTemplate = () => {
    const sheet = XLSX.utils.aoa_to_sheet([
        ['Batch', 'Employee Name', 'Batch KG', 'Rice Item', 'Price'],
        ['Batch 1', 'Dela Cruz, Juan', 25, 'Well-Milled', 45],
        ['Batch 1', 'Santos, Maria', 50, 'Well-Milled', 45],
    ]);
    const instructions = XLSX.utils.aoa_to_sheet([
        ['Column', 'Notes'],
        ['Batch', 'Free-text label kept with each employee. It does not split the upload into separate transactions.'],
        ['Employee Name', 'Must match an active employee of the selected branch. The employee code also works, and is the way to pick between two employees with the same name.'],
        ['Batch KG', 'Between 0.01 and 1,000, at most 2 decimals. Use a number cell. A value that differs from the employee default is recorded as an override ("Monthly Upload").'],
        ['Rice Item', 'Name or code of an existing, active item in the Rice category. New items are never created from the file.'],
        ['Price', 'Price per kg, greater than zero, at most 2 decimals. Use a number cell. Text such as 25,5 is rejected because the comma is ambiguous.'],
        ['After upload', 'The file is saved as a draft. Rows with problems are kept for correction; nothing is deducted from stock until the draft is approved and released.'],
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Monthly Upload');
    XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
    XLSX.writeFile(workbook, 'rice_allowance_monthly_upload_template.xlsx');
};

const RiceAllowanceMonthlyUpload = ({ open, onClose, onCompleted, branches = [], locations = [], items = [] }) => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const [form] = Form.useForm();
    const branchId = Form.useWatch('branch_id', form);
    const [tab, setTab] = useState('upload');
    const [reviewId, setReviewId] = useState(null);
    const [drafts, setDrafts] = useState([]);
    const [loadingDrafts, setLoadingDrafts] = useState(false);
    const [file, setFile] = useState(null);
    const [step, setStep] = useState('form');
    const [reading, setReading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snapshot, setSnapshot] = useState(null); // header + rows exactly as previewed
    const [preview, setPreview] = useState(null);
    const [issuesOnly, setIssuesOnly] = useState(false);

    const branchLocations = useMemo(
        () => locations.filter((location) => Number(location.branch_id) === Number(branchId)),
        [locations, branchId]
    );

    const loadDrafts = useCallback(async () => {
        setLoadingDrafts(true);
        try {
            const { data } = await client.get(`${BASE}/drafts`);
            setDrafts(data || []);
        } catch (error) {
            message.error(apiError(error, 'Failed to load upload drafts'));
        } finally {
            setLoadingDrafts(false);
        }
    }, []);

    const reset = useCallback(() => {
        const firstBranch = branches[0]?.id;
        setFile(null);
        setStep('form');
        setSnapshot(null);
        setPreview(null);
        setIssuesOnly(false);
        form.resetFields();
        form.setFieldsValue({
            allowance_month: dayjs().startOf('month'),
            branch_id: firstBranch,
            location_id: locations.find((location) => Number(location.branch_id) === Number(firstBranch))?.id,
        });
    }, [branches, locations, form]);

    // reset() changes identity whenever the parent reloads branches/locations (every
    // confirm/correct/dismiss does). Initialise only when the dialog opens, so those
    // reloads cannot close the draft being reviewed.
    const resetRef = useRef(reset);
    useEffect(() => { resetRef.current = reset; });

    useEffect(() => {
        if (open) {
            resetRef.current();
            setTab('upload');
            setReviewId(null);
            loadDrafts();
        }
    }, [open, loadDrafts]);

    // Branches/locations that arrive after the dialog opened still fill the empty form defaults.
    useEffect(() => {
        if (open && branches.length && !form.getFieldValue('branch_id')) {
            resetRef.current();
        }
    }, [open, branches, locations, form]);

    const handleBranchChange = (value) => {
        form.setFieldsValue({ location_id: locations.find((location) => Number(location.branch_id) === Number(value))?.id });
    };

    const readAndPreview = async (values) => {
        if (!file) {
            message.error('Choose an Excel file first.');
            return;
        }
        setReading(true);
        try {
            const rows = await parseWorkbook(file);
            const header = {
                allowance_month: values.allowance_month.startOf('month').format('YYYY-MM-DD'),
                branch_id: values.branch_id,
                location_id: values.location_id,
                remarks: values.remarks || null,
            };
            const { data } = await client.post(`${BASE}/preview`, { ...header, rows });
            setSnapshot({ header, rows });
            setPreview(data);
            setIssuesOnly(data.summary.invalid_rows > 0);
            setStep('preview');
        } catch (error) {
            message.error(error.response ? apiError(error, 'Failed to read the file') : error.message);
        } finally {
            setReading(false);
        }
    };

    const saveDraft = async () => {
        setSaving(true);
        try {
            const { data } = await client.post(`${BASE}/commit`, { ...snapshot.header, rows: snapshot.rows });
            const unresolved = data.upload_summary?.unresolved_rows || 0;
            message.success(unresolved
                ? `${data.batch_number} saved as a draft with ${unresolved} row(s) to correct`
                : `${data.batch_number} saved as a draft`);
            onCompleted?.(data);
            reset();
            await loadDrafts();
            setReviewId(data.id);
            setTab('drafts');
        } catch (error) {
            message.error(apiError(error, 'Failed to save the upload'));
        } finally {
            setSaving(false);
        }
    };

    const summary = preview?.summary;
    const tableRows = useMemo(() => {
        const rows = preview?.rows || [];
        return issuesOnly ? rows.filter((row) => row.status !== 'VALID') : rows;
    }, [preview, issuesOnly]);

    const previewColumns = [
        { title: 'Row', dataIndex: 'row', width: 60 },
        { title: 'Batch', dataIndex: 'batch_label', width: 110, render: (value) => value || <Text type="secondary">—</Text> },
        { title: 'Employee Name', dataIndex: 'employee_name', width: 190 },
        {
            title: 'Matched Employee', width: 220,
            render: (_, row) => row.matched_employee
                ? <span>{row.matched_employee.name} <Text type="secondary">[{row.matched_employee.code}]</Text></span>
                : <Text type="secondary">{row.needs_verification ? 'Needs verification' : 'No match'}</Text>,
        },
        { title: 'Batch KG', dataIndex: 'kg', width: 90, align: 'right' },
        { title: 'Rice Item', dataIndex: 'rice_item', width: 160 },
        { title: 'Price', dataIndex: 'price', width: 90, align: 'right' },
        { title: 'Amount', dataIndex: 'line_total', width: 110, align: 'right', render: (value) => (value === null || value === undefined ? '—' : money(value)) },
        {
            title: 'Result', width: 140, fixed: isMobile ? undefined : 'right',
            render: (_, row) => <Space direction="vertical" size={2}>
                <Tag color={statusColor[row.status]} style={{ margin: 0 }}>{row.status === 'INVALID' ? 'NEEDS CORRECTION' : row.status}</Tag>
                {row.needs_verification && <Tag color="orange" style={{ margin: 0 }}>Verify employee</Tag>}
                {row.requires_override && <Tag color="gold" style={{ margin: 0 }}>Override</Tag>}
            </Space>,
        },
        {
            title: 'Validation', width: 340,
            render: (_, row) => row.issues.length
                ? <Space direction="vertical" size={0}>{row.issues.map((issue) => (
                    <Text key={issue.code + issue.message} type={issue.level === 'error' ? 'danger' : 'warning'}>{issue.message}</Text>
                ))}</Space>
                : <Text type="success">OK</Text>,
        },
    ];

    const stockColumns = [
        { title: 'Rice Item', dataIndex: 'item_name' },
        { title: 'Required KG', dataIndex: 'required_kg', align: 'right', render: kgText },
        { title: 'Available KG', dataIndex: 'available_kg', align: 'right', render: (value) => (value === null ? '—' : kgText(value)) },
        { title: 'After Release', dataIndex: 'remaining_kg', align: 'right', render: (value) => (value === null ? '—' : kgText(value)) },
        { title: 'Stock', render: (_, line) => <Tag color={line.sufficient ? 'success' : 'warning'}>{line.sufficient ? 'Sufficient' : 'Short'}</Tag> },
    ];

    const draftColumns = [
        { title: 'Batch', dataIndex: 'batch_number', render: (value) => <Text strong>{value}</Text> },
        { title: 'Month', dataIndex: 'allowance_month', render: (value) => dayjs(value).format('MMMM YYYY') },
        { title: 'Branch', dataIndex: 'branch' },
        { title: 'Recipients', dataIndex: 'total_employees', align: 'right' },
        { title: 'Total KG', dataIndex: 'total_quantity', align: 'right', render: kgText },
        { title: 'Status', dataIndex: 'status', render: (value) => <Tag color={draftStatusColor[value]}>{value}</Tag> },
        {
            title: 'To correct', dataIndex: 'unresolved_rows',
            render: (value, row) => (row.needs_correction ? <Tag color="error">{value} row(s) need correction</Tag> : <Tag color="success">None</Tag>),
        },
        { title: '', render: (_, row) => <Button size="small" onClick={() => setReviewId(row.id)}>Review</Button> },
    ];

    const uploadTab = (
        <>
            <div style={{ display: step === 'form' ? 'block' : 'none' }}>
                <Form form={form} layout="vertical" onFinish={readAndPreview}>
                    <Row gutter={16}>
                        <Col xs={24} md={8}><Form.Item name="allowance_month" label="Allowance Month" rules={[{ required: true }]}><DatePicker picker="month" style={{ width: '100%' }} /></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}><Select options={branches.map((row) => ({ label: row.name, value: row.id }))} onChange={handleBranchChange} /></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="location_id" label="Source Warehouse" rules={[{ required: true }]}><Select options={branchLocations.map((row) => ({ label: row.name, value: row.id }))} /></Form.Item></Col>
                    </Row>
                    <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} maxLength={2000} placeholder="Optional notes about this monthly upload" /></Form.Item>
                    <Form.Item label="Excel File" required>
                        <Space wrap>
                            <Upload
                                accept=".xlsx,.xls,.csv"
                                maxCount={1}
                                fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []}
                                beforeUpload={(selected) => { setFile(selected); return false; }}
                                onRemove={() => setFile(null)}
                            >
                                <Button icon={<UploadOutlined />}>Choose Excel File</Button>
                            </Upload>
                            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>Download Template</Button>
                        </Space>
                        <div><Text type="secondary">Columns: Batch, Employee Name, Batch KG, Rice Item, Price (first sheet, up to {MAX_ROWS} rows). The upload is saved as a draft; nothing is posted.</Text></div>
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={onClose}>Close</Button>
                        <Button type="primary" htmlType="submit" icon={<FileExcelOutlined />} loading={reading}>Read and Preview</Button>
                    </div>
                </Form>
            </div>

            {step === 'preview' && preview && (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Text type="secondary">
                        {dayjs(preview.header.allowance_month).format('MMMM YYYY')} · {preview.header.branch?.name} · {preview.header.location?.name} · {file?.name}
                    </Text>

                    {preview.header.errors.map((text) => <Alert key={text} type="error" showIcon message={text} />)}
                    {preview.duplicate_file && (
                        <Alert type="error" showIcon message={`This exact file was already uploaded for this month and branch as ${preview.duplicate_file.batch_number} (${preview.duplicate_file.status}). Open that draft, or delete it first if you need to upload the file again.`} />
                    )}
                    {summary.invalid_rows > 0 && (
                        <Alert
                            type="warning" showIcon
                            message={`${summary.invalid_rows} row(s) need correction.`}
                            description="You can still save the draft. These rows are kept with their original values and row numbers, and the draft cannot be approved or released until each one is corrected or dismissed."
                        />
                    )}
                    {summary.needs_verification_rows > 0 && (
                        <Alert type="warning" showIcon message={`${summary.needs_verification_rows} employee name(s) matched only by reordering the words. They are not assigned until you confirm them in the draft.`} />
                    )}
                    {summary.existing_allowance_rows > 0 && (
                        <Alert type="info" showIcon message={`${summary.existing_allowance_rows} employee(s) already have an allowance for this month and branch (see the Validation column).`} />
                    )}

                    <Row gutter={[12, 12]}>
                        <Col xs={12} md={6}><Card size="small"><Statistic title="Recipients to import" value={summary.valid_rows} suffix={`/ ${summary.total_rows}`} /></Card></Col>
                        <Col xs={12} md={6}><Card size="small"><Statistic title="Total KG (importable)" value={summary.total_kg} precision={2} /></Card></Col>
                        <Col xs={12} md={6}><Card size="small"><Statistic title="Total Amount (importable)" value={summary.total_amount} precision={2} prefix="₱" /></Card></Col>
                        <Col xs={12} md={6}><Card size="small"><Statistic title="Need correction / Duplicates" value={summary.invalid_rows} suffix={`/ ${summary.duplicate_rows}`} valueStyle={summary.invalid_rows ? { color: '#cf1322' } : undefined} /></Card></Col>
                    </Row>

                    <Card size="small" title="Stock availability by rice item (for the rows that will be imported)">
                        <Table rowKey="item_id" size="small" pagination={false} columns={stockColumns} dataSource={preview.stock} scroll={{ x: 520 }} />
                    </Card>

                    <Card
                        size="small"
                        title={`Rows (${tableRows.length}${issuesOnly ? ' with issues' : ''})`}
                        extra={<Space>Only rows with issues <Switch size="small" checked={issuesOnly} onChange={setIssuesOnly} /></Space>}
                    >
                        <Table
                            rowKey="row" size="small" columns={previewColumns} dataSource={tableRows}
                            pagination={{ pageSize: 25, showSizeChanger: false }} scroll={{ x: 1400 }}
                        />
                    </Card>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                        <Button onClick={() => setStep('form')} disabled={saving}>Choose Another File</Button>
                        <Button onClick={onClose} disabled={saving}>Close</Button>
                        <Button type="primary" icon={<SaveOutlined />} onClick={saveDraft} disabled={!preview.can_save_draft} loading={saving}>
                            Save as Draft
                        </Button>
                    </div>
                </Space>
            )}
        </>
    );

    const draftsTab = reviewId
        ? <RiceAllowanceUploadReview
            batchId={reviewId}
            items={items}
            onBack={() => { setReviewId(null); loadDrafts(); }}
            onChanged={() => { onCompleted?.(); }}
        />
        : <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text type="secondary">Saved uploads. Correct any rows that need it, then approve and release the batch with the normal Rice Allowance process.</Text>
            <Table rowKey="id" size="small" columns={draftColumns} dataSource={drafts} loading={loadingDrafts} pagination={{ pageSize: 10, hideOnSinglePage: true }} scroll={{ x: 800 }} />
        </Space>;

    return (
        <Modal
            title="Monthly Upload"
            open={open}
            onCancel={() => !saving && onClose()}
            width={1200}
            footer={null}
            maskClosable={false}
        >
            <Tabs
                activeKey={tab}
                onChange={(key) => { setTab(key); if (key === 'drafts') loadDrafts(); }}
                items={[
                    { key: 'upload', label: 'New Upload', children: uploadTab },
                    { key: 'drafts', label: `Upload Drafts${drafts.filter((draft) => draft.needs_correction).length ? ` (${drafts.filter((draft) => draft.needs_correction).length} to correct)` : ''}`, children: draftsTab },
                ]}
            />
        </Modal>
    );
};

export default RiceAllowanceMonthlyUpload;
